import { onFormatDate } from 'components'
import { FieldProps } from 'formik'
import { FormikDropdown } from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { Participant, PersonType, Proving } from 'model'
import { IDropdownOption } from 'office-ui-fabric-react'
import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

type ParticipantProp = Pick<
  Participant,
  '_id' | 'person' | 'dateFrom' | 'dateTo' | 'unknownPerson'
>

export interface ProvingDetailsDocumentFormProps {
  participants: ParticipantProp[]
  proving: Proving
  provingGround: any
}

export class ProvingDetailsDocumentFormSchema {
  constructor() {
    return Yup.object().shape({
      type: Yup.string().required(),
      participantId: Yup.string().when('type', {
        is: (type) =>
          [
            'bookingConfirmationHotel',
            'bookingConfirmationRentalCar',
            'bookingConfirmationFerry',
            'bookingConfirmationFlight',
            'bookingConfirmationRouteHotel',
          ].includes(type),
        then: Yup.string().required('hilfe'),
        otherwise: Yup.string().notRequired().nullable(),
      }),
    })
  }
}

export const ProvingDetailsDocumentForm: React.FunctionComponent<
  ProvingDetailsDocumentFormProps
> = (props) => {
  const { t } = useTranslation()
  const [, setState] = useState()

  const options: IDropdownOption[] = [
    {
      key: 'bookingConfirmationHotel',
      text: t('enum.documentType.bookingConfirmationHotel'),
    },
    {
      key: 'bookingConfirmationRentalCar',
      text: t('enum.documentType.bookingConfirmationRentalCar'),
    },
  ]

  if (!(props.provingGround && props.provingGround.hideFerryConnection)) {
    options.push({
      key: 'bookingConfirmationFerry',
      text: t('enum.documentType.bookingConfirmationFerry'),
    })
  }
  if (!(props.provingGround && props.provingGround.hideFlight)) {
    options.push({
      key: 'bookingConfirmationFlight',
      text: t('enum.documentType.bookingConfirmationFlight'),
    })
  }
  if (!(props.provingGround && props.provingGround.hideHotels)) {
    options.push({
      key: 'bookingConfirmationRouteHotel',
      text: t('enum.documentType.bookingConfirmationRouteHotel'),
    })
  }

  if (props.proving.provingGround && props.proving.provingGround.block1) {
    options.push({
      key: props.proving.provingGround.block1,
      text: props.proving.provingGround.block1,
    })
  }
  if (props.proving.provingGround && props.proving.provingGround.block2) {
    options.push({
      key: props.proving.provingGround.block2,
      text: props.proving.provingGround.block2,
    })
  }
  if (props.proving.provingGround && props.proving.provingGround.block3) {
    options.push({
      key: props.proving.provingGround.block3,
      text: props.proving.provingGround.block3,
    })
  }
  if (props.proving.provingGround && props.proving.provingGround.block4) {
    options.push({
      key: props.proving.provingGround.block4,
      text: props.proving.provingGround.block4,
    })
  }
  if (props.proving.provingGround && props.proving.provingGround.block5) {
    options.push({
      key: props.proving.provingGround.block5,
      text: props.proving.provingGround.block5,
    })
  }
  if (props.proving.provingGround && props.proving.provingGround.block6) {
    options.push({
      key: props.proving.provingGround.block6,
      text: props.proving.provingGround.block6,
    })
  }
  if (props.proving.provingGround && props.proving.provingGround.block7) {
    options.push({
      key: props.proving.provingGround.block7,
      text: props.proving.provingGround.block7,
    })
  }
  if (props.proving.provingGround && props.proving.provingGround.block8) {
    options.push({
      key: props.proving.provingGround.block8,
      text: props.proving.provingGround.block8,
    })
  }
  if (props.proving.provingGround && props.proving.provingGround.block9) {
    options.push({
      key: props.proving.provingGround.block9,
      text: props.proving.provingGround.block9,
    })
  }
  if (props.proving.provingGround && props.proving.provingGround.block10) {
    options.push({
      key: props.proving.provingGround.block10,
      text: props.proving.provingGround.block10,
    })
  }

  return (
    <FormStack>
      <FieldStack>
        <FieldStackItem
          name="type"
          render={(fieldProps: FieldProps<any>) => (
            <FormikDropdown
              label={t('field.mailTemplate.documentType')}
              {...fieldProps}
              options={options}
              required={true}
              onChanged={(option) => {
                setState(option.key)
              }}
            />
          )}
        />
        <FieldStackItem
          name="participantId"
          render={(fieldProps: FieldProps<any>) => (
            <FormikDropdown
              label={t('field.mailTemplate.participant')}
              {...fieldProps}
              options={props.participants
                .filter(
                  (y) =>
                    y.person ||
                    (y.unknownPerson &&
                      (y.unknownPerson.personType === PersonType.User ||
                        y.unknownPerson.personType === PersonType.External ||
                        y.unknownPerson.personType === PersonType.Worker))
                )
                .map((z) => {
                  let name = ''
                  if (z.person) {
                    name = `${z.person.lastName}, ${z.person.firstName}`
                  } else if (z.unknownPerson && !z.unknownPerson.isUnknown) {
                    name = `${z.unknownPerson.surName}, ${z.unknownPerson.firstName}`
                  } else if (
                    z.unknownPerson &&
                    z.unknownPerson.isUnknown &&
                    z.unknownPerson.department
                  ) {
                    name = z.unknownPerson.department
                  } else {
                    name = '[Teilnehmer noch unbekannt]'
                  }
                  return {
                    key: z._id,
                    text: `${name} (${onFormatDate(
                      new Date(z.dateFrom)
                    )} - ${onFormatDate(new Date(z.dateTo))})`,
                  }
                })
                .sort((a, b) =>
                  a.text
                    .toLocaleLowerCase()
                    .localeCompare(b.text.toLocaleLowerCase())
                )}
              required={false}
            />
          )}
        />
        {/* {state !== 'bookingConfirmationHotel' &&
          state !== 'bookingConfirmationRentalCar' &&
          state !== 'bookingConfirmationFerry' &&
          state !== 'bookingConfirmationFlight' &&
          state !== 'bookingConfirmationRouteHotel' && (
            <FieldStackItem
              name="block"
              render={(fieldProps: FieldProps<any>) => (
                <FormikDropdown
                  label={t('Datenablage')}
                  {...fieldProps}
                  options={blocks}
                  required={false}
                />
              )}
            />
          )} */}
      </FieldStack>
    </FormStack>
  )
}
